import { EditorState, Modifier, SelectionState } from 'draft-js';
import { AlignCenter, AlignJustify, AlignLeft, AlignRight } from 'lucide-react';

interface TextAlignButtonsProps {
  editorState?: EditorState;
  onChange?: (editorState: EditorState) => void;
}

type Alignment = 'left' | 'center' | 'right' | 'justify';

const getSelectedBlockKeys = (editorState: EditorState): string[] => {
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const blockMap = content.getBlockMap();
  const keys: string[] = [];
  let inRange = false;

  blockMap.forEach((_, key) => {
    if (key === startKey) inRange = true;
    if (inRange) keys.push(key!);
    if (key === endKey) inRange = false;
  });

  return keys;
};

const getCurrentAlignment = (editorState: EditorState): Alignment => {
  const startKey = editorState.getSelection().getStartKey();
  const block = editorState.getCurrentContent().getBlockForKey(startKey);
  return (block?.getData().get('text-align') as Alignment) || 'left';
};

const applyAlignment = (
  editorState: EditorState,
  alignment: Alignment,
): EditorState => {
  const blockKeys = getSelectedBlockKeys(editorState);
  let contentState = editorState.getCurrentContent();
  const value = alignment === 'left' ? undefined : alignment;

  for (const key of blockKeys) {
    const block = contentState.getBlockForKey(key);
    if (!block) continue;

    const blockSelection = SelectionState.createEmpty(key).merge({
      anchorKey: key,
      focusKey: key,
      anchorOffset: 0,
      focusOffset: block.getLength(),
    }) as SelectionState;

    const newData = block.getData().set('text-align', value);
    contentState = Modifier.setBlockData(contentState, blockSelection, newData);
  }

  const newState = EditorState.push(
    editorState,
    contentState,
    'change-block-data',
  );
  return EditorState.forceSelection(newState, editorState.getSelection());
};

const alignments: {
  value: Alignment;
  icon: typeof AlignLeft;
  label: string;
}[] = [
  { value: 'left', icon: AlignLeft, label: 'Align left' },
  { value: 'center', icon: AlignCenter, label: 'Align center' },
  { value: 'right', icon: AlignRight, label: 'Align right' },
  { value: 'justify', icon: AlignJustify, label: 'Justify' },
];

const TextAlignButtons = ({ editorState, onChange }: TextAlignButtonsProps) => {
  if (!editorState || !onChange) return null;

  const currentAlignment = getCurrentAlignment(editorState);

  return (
    <div className="rdw-text-align-wrapper" style={{ display: 'flex' }}>
      {alignments.map(({ value, icon: Icon, label }) => {
        const isActive = currentAlignment === value;
        return (
          <div
            key={value}
            className={`rdw-option-wrapper ${
              isActive ? 'rdw-option-active' : ''
            }`}
            onClick={() => onChange(applyAlignment(editorState, value))}
            title={label}
          >
            <Icon size={15} />
          </div>
        );
      })}
    </div>
  );
};

export default TextAlignButtons;
